import { refs } from '../core/refs.js';
import { syncCustomSelect } from './custom-select.js';
import {
  DEFAULT_DEVICE_PROFILE_ID,
  getDeviceProfile,
  listDeviceProfiles,
  normalizeCustomPreviewFrame,
  swapPreviewFrameOrientation
} from './preview-device-profiles.js';
import {
  getPreviewFrameState,
  getPreviewFrameTarget,
  setCustomPreviewFrame,
  setDeviceProfile
} from './preview-presets.js';

let registered = false;

function profileOptions() {
  const profileOptionsList = listDeviceProfiles().map((profile) => new Option(profile.label, profile.id));
  profileOptionsList.push(new Option('Custom Screen Size', 'custom'));
  return profileOptionsList;
}

function setError(message = '') {
  const error = refs.previewFrameError;
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

function activeProfile() {
  return getDeviceProfile(refs.previewFrameProfileSelect?.value || DEFAULT_DEVICE_PROFILE_ID);
}

function frameFromFields() {
  return normalizeCustomPreviewFrame({
    label: refs.previewFrameLabelInput?.value,
    width: refs.previewFrameWidthInput?.value,
    height: refs.previewFrameHeightInput?.value,
    dpr: refs.previewFrameDprInput?.value
  });
}

function fillFields(frame) {
  if (!frame) return;
  if (refs.previewFrameLabelInput) refs.previewFrameLabelInput.value = frame.label || 'Custom Screen';
  if (refs.previewFrameWidthInput) refs.previewFrameWidthInput.value = String(frame.width || 390);
  if (refs.previewFrameHeightInput) refs.previewFrameHeightInput.value = String(frame.height || 844);
  if (refs.previewFrameDprInput) refs.previewFrameDprInput.value = String(frame.dpr || 2);
  if (refs.previewFrameNote) refs.previewFrameNote.textContent = frame.note || 'Local layout simulation only. Rendering still uses the current browser engine.';
}

function setFieldEditability(editable) {
  [
    refs.previewFrameLabelInput,
    refs.previewFrameWidthInput,
    refs.previewFrameHeightInput,
    refs.previewFrameDprInput
  ].forEach((input) => {
    if (input) input.disabled = !editable;
  });
}

function syncFieldsFromSelection() {
  setError('');
  const select = refs.previewFrameProfileSelect;
  if (!select) return;

  if (select.value === 'custom') {
    const target = getPreviewFrameTarget();
    fillFields(target || normalizeCustomPreviewFrame({}));
    setFieldEditability(true);
    return;
  }

  const profile = activeProfile();
  fillFields(profile);
  setFieldEditability(false);
}

function ensureProfileOptions() {
  const select = refs.previewFrameProfileSelect;
  if (!select) return;
  if (!select.options.length) {
    select.replaceChildren(...profileOptions());
  }
}

function selectedProfileForCurrentState() {
  const state = getPreviewFrameState();
  if (state.mode === 'device' && getDeviceProfile(state.profileId)) return state.profileId;
  return DEFAULT_DEVICE_PROFILE_ID;
}

function openModal(startMode) {
  const modal = refs.previewFrameModal;
  const select = refs.previewFrameProfileSelect;
  if (!modal || !select) return;

  ensureDialogEvents();
  ensureProfileOptions();
  select.value = startMode === 'custom' ? 'custom' : selectedProfileForCurrentState();
  syncCustomSelect(select);
  syncFieldsFromSelection();

  if (typeof modal.showModal === 'function') {
    modal.showModal();
  }
}

function applySelection() {
  const select = refs.previewFrameProfileSelect;
  if (!select) return;
  setError('');

  if (select.value === 'custom') {
    const frame = frameFromFields();
    setCustomPreviewFrame(frame);
    refs.previewFrameModal?.close();
    return;
  }

  if (!setDeviceProfile(select.value)) {
    setError('Choose a supported preview frame.');
    return;
  }
  refs.previewFrameModal?.close();
}

function swapFields() {
  setError('');
  const select = refs.previewFrameProfileSelect;
  if (!select) return;

  const swapped = swapPreviewFrameOrientation(frameFromFields());
  select.value = 'custom';
  syncCustomSelect(select);
  fillFields(swapped);
  setFieldEditability(true);
}

function ensureDialogEvents() {
  if (registered) return;
  registered = true;

  refs.previewFrameProfileSelect?.addEventListener('change', () => {
    syncFieldsFromSelection();
    syncCustomSelect(refs.previewFrameProfileSelect);
  });
  refs.previewFrameCancelBtn?.addEventListener('click', () => refs.previewFrameModal?.close());
  refs.previewFrameSwapBtn?.addEventListener('click', swapFields);
  refs.previewFrameApplyBtn?.addEventListener('click', applySelection);
}

export function openPreviewAdvanced(action = 'advanced') {
  if (action === 'custom-size') {
    openModal('custom');
    return;
  }

  openModal('device');
}
